import React, { useEffect, useRef, useState } from 'react';
import { Button, Divider, Input, InputNumber, Modal, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { notification } from 'antd';
import { EditOutlined, EyeOutlined, LockFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from './Production.redux';
import useDebounce from 'hooks/useDebounce';
import ProductionModal from './ProductionModal';
import { SearchOutlined, EnterOutlined, BoxPlotOutlined, EditFilled, PlusOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import ___ from '../constants';
import LoadingCover from 'components/LoadingCover';
import sizeSorter from '../shared/sizeSorter';
import sizeFilter from '../shared/sizeFilter';
import { extractColumnDataIndexes } from '../shared/searchUtilites';
import { Link } from 'react-router-dom';
import NotesPopover from './NotesPopover';
import NotesModal from './NotesModal';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const { Title } = Typography;

const DEBOUNCE_TIME = 900;

const isProductionOrderComplete = (r) => {
    if (r.fgCompletedQty >= r.fgPlannedQty) {
        return true;
    }
    return false;
};

const StatusRender = (_, r) => {
    // console.log('[ProductionHeaders] StatusRender');
    // console.log(_); // value of proStatus
    // console.log(r); // record

    if (r.isClosed === ___.TRUE) {
        return <Tag color="grey">Closed</Tag>;
    } if (r.isClosed === ___.FALSE && isProductionOrderComplete(r)) {
        return <Tag color="blue">Complete</Tag>;
    } else if (r.openLines > 0 && r.fgCompletedQty > 0) {
        return <Tag color="orange">Partial</Tag>;
    } else if (r.issuedQty > 0) {
        return <Tag color="orange">Partial</Tag>;
    } else if (r.openLines >= 0 && r.fgCompletedQty === 0) {
        return <Tag color="red">Pending</Tag>;
    }
};

const docStatusFilter = (value, record) => {
    // console.log(value);
    // console.log(record);

    // STATUS_FILTER_OPTIONS:  Open [pending + complete], Partial, Pending, Closed, Completed
    // proStatus:  C=Canceled, L=Closed, P=Planned, R=Released
    // lineStatus: C=Complete, I=In Progress, P=Planned

    if (value === 'Completed' && record.proStatus !== ___.PRO_CLOSED && isProductionOrderComplete(record)) {
        return true;
    } else if (value === 'Partial' && record.proStatus === ___.PRO_OPEN && !isProductionOrderComplete(record) && ((record.issuedQty + record.fgCompletedQty) > 0)) {
        return true;
    } else if (value === 'Pending' && record.proStatus === ___.PRO_OPEN && record.openLines > 0) {
        return true;
    } else if (value === 'Closed' && record.proStatus === ___.PRO_CLOSED) {
        return true;
    } else if (value === 'Closed' && record.proStatus !== ___.PRO_CLOSED) {
        return false;
        // } else if (value === 'Open' && record.proStatus === ___.PRO_OPEN) {
        //     return true;
        // } else if (value === 'Open' && record.proStatus === ___.PRO_CLOSED) {
        //     return false;
        // 
    }
};

const DefaultWarehouseFilter = (value, record) => {
    if (value === 'All') return true;

    return record.defaultWarehouse === value;
};

const ProductionHeaders = () => {

    const dispatch = useDispatch(),
        navigate = useHistory(),
        [searchTerm, setSearchTerm] = useState(''),
        debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_TIME),
        [notesVisible, setNotesVisible] = useState(false),
        [pendingSearch, setPendingSearch] = useState(null),
        //logged_in_rep = useSelector(state => state.warehouseProd.logged_in_rep),
        //warehouse = useSelector(state => state.warehouseProd),
        lastSelectedDocEntry = useSelector(state => state.warehouseProd.lastSelectedDocEntry),
        productionOrderHeaders = useSelector(state => state.warehouseProd.filteredHeaders), // PROheaders
        warehouseOptions = useSelector(state => state.warehouseProd.warehouseOptions),
        failedDocEntriesBatch = useSelector(state => state.warehouseProd.failedDocEntriesBatch),
        errors = useSelector(state => state.warehouseProd.errors),
        loading = useSelector(state => state.warehouseProd.loading),
        searchLoading = useSelector(state => state.warehouseProd.searchLoading);


    const [editProductionOrderHeaders, setEditProductionOrderHeaders] = useState(productionOrderHeaders);

    let pageSize = 50;

    const warehouseFilters = React.useMemo(() => {
        if (!warehouseOptions || warehouseOptions.length === 0) return [];

        return [
            { text: 'All', value: 'All' },
            ...warehouseOptions.map(opt => ({
                text: opt.FldValue,   // display
                value: opt.FldValue,  // filter value
            }))
        ];
    }, [warehouseOptions]);

    useEffect(() => {
        console.log('[ProductionHeaders.js] onload');
        dispatch(actions.get_warehouse_options());
        dispatch(actions.getProductionOrderHeaders());
    }, []);

    useEffect(() => {
        console.log('[ProductionHeaders.js] loading change: ' + loading);
        if (!loading && pendingSearch !== null) {
            onSearch(pendingSearch);
            setPendingSearch(null); // Clear it after running
        }
    }, [loading]);


    useEffect(() => {
        console.log('[ProductionHeaders.js] productionOrderHeaders change');
        //console.log(productionOrderHeaders)
        setEditProductionOrderHeaders(productionOrderHeaders);
    }, [
        dispatch,
        productionOrderHeaders,
    ]);

    useEffect(() => {
        console.log('[ProductionHeaders.js] debouncedSearchTerm change');
        // console.log(productionOrderHeaders);
        // console.log(productionOrderHeaders.length);
        if ((!productionOrderHeaders || productionOrderHeaders.length === 0) || !debouncedSearchTerm && debouncedSearchTerm !== '') {
            return;
        }

        if (debouncedSearchTerm.trim() === '') {
            if (!loading) {
                //console.log('[ProductionHeaders.js] cleared search term');
                onSearch('');
            } else {
                setPendingSearch('');
            }
            return;
        }

        if (loading) {
            //console.log('[ProductionHeaders.js] Queuing search during loading');
            setPendingSearch(debouncedSearchTerm);
        } else {
            //console.log('[ProductionHeaders.js] Triggering onSearch for', debouncedSearchTerm);
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm]);

    const [tableSearchText, setTableSearchText] = useState('');
    const [tableSearchedColumn, setTableSearchedColumn] = useState('');
    const searchInput = useRef(null);


    const handleTableSearch = ( // table header search
        selectedKeys,
        dataIndex,
    ) => {
        setTableSearchText(selectedKeys[0]);
        setTableSearchedColumn(dataIndex);
    };


    const onSearch = (value) => {
        // console.log('onSearch ' + value);

        setSearchTerm(value);
        dispatch(actions.searchCurrentHeaders({
            searchValue: value,
            excludeList: ['proStatus', 'size', 'arrivalDate', 'fgPlannedQty', 'plannedQty', 'IssueType',
                'fgCompletedQty', 'onHandQty', 'defaultWarehouse', 'qtyToIssue', 'finish-button'],
            includeList: searchable,
            exactList: ['orderNum']
        }));
    };


    const handleReset = (clearFilters = console.log('handleReset')) => {
        // console.log('handleReset')
        clearFilters();
        setTableSearchText('');
    };

    const getColumnSearchProps = (dataIndex, friendlySearch) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${friendlySearch}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value
                        ? [e.target.value]
                        : [])}
                    onPressEnter={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="large"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="large"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="large"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setTableSearchText((selectedKeys)[0]);
                            setTableSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="large"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: (filtered) => (
            <SearchOutlined style={{
                color: filtered
                    ? '#1677ff'
                    : undefined,
            }} />
        ),

        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value).toLowerCase()),

        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            tableSearchedColumn === dataIndex
                ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[tableSearchText]}
                        autoEscape
                        textToHighlight={text
                            ? text.toString()
                            : ''}
                    />
                )
                : (
                    text
                ),
    });

    const handleSelectNotes = (record) => {
        console.log('handleSelectNotes', record)
        dispatch(actions.setCurrentEditRow(record));
        setNotesVisible(true)
    };


    const columns = [
        // { // DEBUG 
        //     title: 'KEY', dataIndex: 'docKey', key: 'docKey',
        //     render: (_, record) => (
        //         (record.hasChanges) ?
        //             <Tag color="red">{record.docKey}</Tag>
        //             :
        //             <Tag color="grey">{record.docKey}</Tag>
        //     ),
        // },
        {
            title: 'PRO#', dataIndex: 'orderNum', key: 'orderNum',
            width: 100,
            ...getColumnSearchProps('orderNum', 'PRO#'),
        },
        {
            title: 'Original SO#',
            dataIndex: 'origSO',
            key: 'origSO',
            width: 100,
            render: (value, record, index) => (
                <Link
                    to={`/orders/edit/order/${record.SODocEntry}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Button>
                        {record.origSO}
                    </Button>
                </Link>
            ),
        },
        {
            title: 'Customer Name', dataIndex: 'bpName', key: 'bpName',
            width: 200,
            ...getColumnSearchProps('bpName', 'Customer'),
        },
        { title: 'Finished Product Name', dataIndex: 'fgProdName', key: 'fgProdName', width: 350 },

        {
            title: 'Project',
            dataIndex: 'project',
            key: 'project',
            width: 200,
            render: (_, record) => {
                return record.project3 !== ''
                    ? record.project3 :
                    record.project2
                        ? record.project2
                        : record.project
                            ? record.project
                            : ___.NOT_AVAILABLE;
            },
        },
        {
            title: 'PRO Status', dataIndex: 'proStatus', key: 'proStatus',
            width: 200,
            align: 'center',
            //defaultFilteredValue: ['Pending'],
            filters: [ //STATUS_FILTER_OPTIONS
                // {
                //     text: 'Open',
                //     value: 'Open',
                // },
                {
                    text: 'Pending',
                    value: 'Pending',
                },
                {
                    text: 'Partial',
                    value: 'Partial',
                },
                {
                    text: 'Closed',
                    value: 'Closed',
                },
                {
                    text: 'Completed',
                    value: 'Completed',
                },
            ],
            onFilter: docStatusFilter,
            render: StatusRender,
            filterMultiple: false,
            // ...getColumnSearchProps('proStatus', 'PRO Status'),
        },

        {
            //U_TS_DefaultWarehouse
            //defaultFilteredValue: ['BB'],
            title: 'Warehouse',
            dataIndex: 'defaultWarehouse',
            key: 'defaultWarehouse',
            width: 80,
            filters: warehouseFilters,
            onFilter: DefaultWarehouseFilter,
            filterMultiple: false,
            render: (_, record) => {
                const match = warehouseOptions?.find(
                    opt => opt.FldValue === record.defaultWarehouse
                );

                return match ? (
                    <Tag color="default">{match.FldValue}</Tag>
                ) : (
                    <Tag color="error">None</Tag>
                );
            },
        },
        {
            title: 'Notes',
            dataIndex: 'add_edit_notes',
            key: 'add_edit_notes',
            width: 150,
            render: (_, record) => {
                return (
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleSelectNotes(record)} > {
                            'View Notes'
                        }</Button>
                )
            },
        },
        // { title: 'Item Code', dataIndex: 'itemCode', key: 'itemCode' },
        // { title: 'Over/Under', dataIndex: 'OverUnder', key: 'OverUnder' },
        // { title: 'Over/Under Line', dataIndex: 'OverUnder_RDR1', key: 'OverUnder_RDR1' },
        {
            title: 'Select / Edit',
            dataIndex: 'edit-select-button',
            render: (_, record) =>
            (
                <Button icon={<EditOutlined />} onClick={() => selectRow(record)} tabIndex={-1}>
                    Select / Edit
                </Button>
            ),
        },
    ];

    const selectRow = (record) => {
        console.log('selectRow', record);
        navigate.push(`/warehouse/production/${record.docNum}`);
    };

    let searchable = extractColumnDataIndexes(columns);
    //console.log(searchable);

    const handleFocus = (event) => {
        console.log('handleFocus');
    };

    const handleBlur = (event) => {
        console.log('blur');
    };

    const handleChange = (event) => {
        //console.log('handleChange ', event.target.value);
        setSearchTerm(event.target.value);
    };

    const handleCloseNotes = () => {
        console.log('[ProductionHeaders] handleCloseNotes');
        setNotesVisible(false);
    };

    return (
        <>
            <Modal
                open={notesVisible}
                footer={null}
                maskClosable={true}
                closable={false}
                style={{
                    minWidth: 1200,
                    maxWidth: '100%',
                }}
            >
                <NotesModal handleClose={handleCloseNotes} handleSave={null} viewOnly={true} />
            </Modal>

            <Title level={3}>Production</Title>

            <Input.Search
                name="production_id"
                className="ts_disabled_alternate"
                allowClear
                enterButton
                size="large"
                placeholder="BP, Doc#, Finished Good, Item, or Project"
                onSearch={onSearch}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                data-skip-blur-update={true}
                value={searchTerm}
                disabled={loading}
                prefix={<Spin spinning={searchLoading} />}
            />

            <Table
                columns={columns}
                rowClassName="editable-row"
                dataSource={editProductionOrderHeaders}
                rowKey="docKey"
                pagination={{
                    position: ['topRight'],
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showSizeChanger: true,
                }}

            />

            {loading && <LoadingCover />}

        </>
    );

};

export default ProductionHeaders;
